/**
 * /growth/ref-links/new — create form. Cycle 15.
 *
 * Pick an IG account, give the link a name + payload, optionally pick
 * a flow to start, and list tags to add. Redirects to the edit page
 * on success so the user can immediately copy the URL or QR.
 */
import { useEffect, useState, type FormEvent } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { api } from "../../../../lib/api.js";
import { useAuth } from "../../../../lib/auth-context.js";

interface FlowRow {
  id: string;
  name: string;
  publishedVersionId: string | null;
}

export const Route = createFileRoute("/_app/growth/ref-links/new")({
  component: NewRefLinkPage,
});

function NewRefLinkPage() {
  const { activeIgAccount } = useAuth();
  const navigate = useNavigate();
  const [flows, setFlows] = useState<FlowRow[]>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [payload, setPayload] = useState("");
  const [flowId, setFlowId] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ flows: FlowRow[] }>("/flows")
      .then((fl) => setFlows(fl.data.flows.filter((f) => f.publishedVersionId)))
      .catch(() => setError("Failed to load flows"));
  }, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || !payload.trim() || !activeIgAccount || busy) return;
    setBusy(true);
    setError(null);
    try {
      const tagsToAdd = tagsInput
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      const r = await api.post<{ refLink: { id: string } }>("/ref-links", {
        igAccountId: activeIgAccount.id,
        name: name.trim(),
        description: description.trim() || null,
        payload: payload.trim(),
        flowId: flowId || null,
        tagsToAdd,
        enabled,
      });
      void navigate({
        to: "/growth/ref-links/$refLinkId",
        params: { refLinkId: r.data.refLink.id },
      });
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } }).response
        ?.data?.message;
      setError(msg ?? "Failed to create");
      setBusy(false);
    }
  }

  return (
    <section style={{ maxWidth: 600, margin: "0 auto" }}>
      <Link to="/growth/ref-links" style={{ fontSize: 13 }}>
        ← Ref links
      </Link>
      <h1 style={{ margin: "4px 0 24px", fontSize: 28 }}>New ref link</h1>
      <div style={{ fontSize: 13, marginBottom: 12, color: "var(--text-muted)" }}>
        Routing to <strong>@{activeIgAccount?.igUsername}</strong>
      </div>
      <form
        onSubmit={create}
        className="brut-card"
        style={{ padding: 14, display: "grid", gap: 10 }}
      >
        <label className="brut-label">Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. summer-promo"
          className="brut-input"
          maxLength={120}
          required
        />
        <label className="brut-label">Description (optional)</label>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Where this link is being shared"
          className="brut-input"
          maxLength={500}
        />
        <label className="brut-label">Payload (URL token)</label>
        <input
          value={payload}
          onChange={(e) => setPayload(e.target.value)}
          placeholder="summer25"
          className="brut-input"
          pattern="[a-zA-Z0-9_-]{1,64}"
          maxLength={64}
          required
        />
        <div style={{ fontSize: 12, color: "var(--muted)" }}>
          1–64 characters: letters, numbers, _ or -. This becomes
          <code> ?ref=&lt;payload&gt;</code> on the link.
        </div>
        <label className="brut-label">Start flow on first DM (optional)</label>
        <select
          value={flowId}
          onChange={(e) => setFlowId(e.target.value)}
          className="brut-input"
        >
          <option value="">(none)</option>
          {flows.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
        <label className="brut-label">Tags to add (comma-separated)</label>
        <input
          value={tagsInput}
          onChange={(e) => setTagsInput(e.target.value)}
          placeholder="campaign:summer, source:print"
          className="brut-input"
        />
        <label
          style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}
        >
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          Enabled
        </label>
        {error ? (
          <div style={{ color: "#b40000", fontSize: 13 }}>{error}</div>
        ) : null}
        <button
          type="submit"
          disabled={!name.trim() || !payload.trim() || !activeIgAccount || busy}
          className="brut-button"
        >
          {busy ? "Creating…" : "Create"}
        </button>
      </form>
    </section>
  );
}
